import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';

const serializeFilters = (filters: Record<string, unknown>) =>
  qs.stringify(
    Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined),
    ),
    { addQueryPrefix: true },
  );

const useQueryFilters = <T extends Record<string, unknown>>(
  parseFilters: (query: Record<string, unknown>) => T,
) => {
  const history = useHistory();
  const location = useLocation();
  const serialized = JSON.stringify(
    parseFilters(qs.parse(location.search, { ignoreQueryPrefix: true })),
  );
  const filters: T = useMemo(() => JSON.parse(serialized), [serialized]);

  useEffect(() => {
    const search = serializeFilters(filters);
    if (history.location.search !== search) {
      history.replace({ ...history.location, search });
    }
  }, [filters, history]);

  const setFilters = useCallback(
    (nextFilters: T) => {
      const search = serializeFilters(nextFilters);
      if (history.location.search !== search) {
        history.push({ ...history.location, search });
      }
    },
    [history],
  );

  return { filters, setFilters };
};

export default useQueryFilters;
